// Charts and progress indicators for the reinsurance screens.
//
// Hand-drawn SVG rather than a charting library. Four shapes are needed —
// a ring, a donut, a stacked column and a horizontal bar list — and none of
// them animates, zooms or handles a hundred thousand points. A library would
// add a dependency, a bundle, and a theme to fight, to draw four shapes.
//
// Every one of them takes its colours from the same `C` palette the rest of
// the module uses, so a chart cannot end up the only thing on the page in a
// different burgundy.

import React from 'react';
import { C, money0, moneyShort } from './kit';

// The series colours, in the order segments are handed out. Burgundy leads
// because it is the module's own colour; the rest step away from it in hue
// rather than in lightness, so adjacent segments stay apart for a reader who
// does not distinguish red and green.
export const SERIES = [
  '#8a1538', '#b4476b', '#d98fa6', '#1d4ed8', '#4b8bf5', '#93b4f7',
  '#0f766e', '#5eaaa3', '#a16207', '#d4a72c',
];
const seriesColour = (i) => SERIES[i % SERIES.length];

// ─── Ring ─────────────────────────────────────────────────────
// "8 of 10 documents uploaded". A ring rather than a bar because the drawer
// puts it beside a label rather than inside a table column, and at that size
// a ring reads as a proportion at a glance where a 60px bar does not.
export function Ring({ done = 0, total = 0, size = 44, stroke = 5, tone }) {
  const pct = total > 0 ? Math.max(0, Math.min(1, done / total)) : 0;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const colour = tone || (pct >= 1 ? C.bar : pct >= 0.5 ? '#f59e0b' : '#ef4444');
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role='img'
      aria-label={`${done} of ${total}`}
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill='none' stroke={C.barTrack} strokeWidth={stroke} />
      {/* Rotated so the arc starts at twelve o'clock rather than three. */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={colour}
        strokeWidth={stroke}
        strokeLinecap='round'
        strokeDasharray={`${circumference * pct} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

// ─── Score bar ────────────────────────────────────────────────
// A validation score out of 100, as the claims list shows it: the bar and
// the number together, because the bar alone cannot be read back and the
// number alone cannot be scanned down a column.
export function ScoreBar({ score, width = 64 }) {
  if (score === null || score === undefined) {
    return <span style={{ color: C.faint, fontSize: 12.5 }}>—</span>;
  }
  const n = Math.max(0, Math.min(100, Number(score)));
  const colour = n >= 85 ? C.bar : n >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ display: 'inline-block', width, height: 6, borderRadius: 999, background: C.barTrack, overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${n}%`, height: '100%', background: colour, borderRadius: 999 }} />
      </span>
      <span style={{ fontSize: 12, color: C.body, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {n}/100
      </span>
    </span>
  );
}

// ─── Donut ────────────────────────────────────────────────────
// Portfolio mix by line of business, with the total in the middle and the
// legend beside it. Segments are drawn as stroked arcs on one circle rather
// than as paths, which keeps the maths to one number per segment.
export function Donut({ segments = [], total, currency, caption, size = 190 }) {
  const values = segments.filter(s => Number(s.amount) > 0);
  const sum = values.reduce((a, s) => a + Number(s.amount), 0);
  const stroke = 30;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;

  // Nothing to draw. Say so rather than rendering an empty ring, which reads
  // as a chart that failed to load.
  if (!sum) {
    return <div style={{ color: C.faint, fontSize: 12.5, padding: '28px 0', textAlign: 'center' }}>No premium in this window.</div>;
  }

  let offset = 0;
  const arcs = values.map((s, i) => {
    const frac = Number(s.amount) / sum;
    const arc = {
      key: s.label,
      colour: seriesColour(i),
      dash: `${circumference * frac} ${circumference}`,
      rotation: (offset * 360) - 90,
    };
    offset += frac;
    return arc;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }} role='img' aria-label='Portfolio mix by line of business'>
        {arcs.map(a => (
          <circle
            key={a.key}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill='none'
            stroke={a.colour}
            strokeWidth={stroke}
            strokeDasharray={a.dash}
            transform={`rotate(${a.rotation} ${size / 2} ${size / 2})`}
          />
        ))}
        <text x={size / 2} y={size / 2 - 6} textAnchor='middle' style={{ fontSize: 11, fill: C.muted }}>
          {currency || ''}
        </text>
        <text x={size / 2} y={size / 2 + 12} textAnchor='middle' style={{ fontSize: 15, fontWeight: 700, fill: C.ink }}>
          {moneyShort(total ?? sum, '')}
        </text>
        {caption && (
          <text x={size / 2} y={size / 2 + 28} textAnchor='middle' style={{ fontSize: 10, fill: C.faint }}>
            {caption}
          </text>
        )}
      </svg>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7, minWidth: 150, flex: 1 }}>
        {values.map((s, i) => (
          <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: seriesColour(i), flexShrink: 0 }} />
            <span style={{ color: C.body, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.label}
            </span>
            <span style={{ color: C.ink, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {s.percent === null || s.percent === undefined ? '—' : `${Math.round(s.percent)}%`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Stacked columns ──────────────────────────────────────────
// Ceded premium by month, split proportional / non-proportional.
//
// The y-axis is labelled with values the chart actually reaches, and the
// scale is rounded UP to a round number so the tallest column never touches
// the top of the plot — a bar flush with the frame reads as clipped.
export function StackedBars({ months = [], currency, height = 180, series }) {
  const keys = series || [
    { key: 'proportional', label: 'Proportional', colour: '#8a1538' },
    { key: 'nonProportional', label: 'Non-Proportional', colour: '#d98fa6' },
  ];
  const totals = months.map(m => keys.reduce((a, k) => a + (Number(m[k.key]) || 0), 0));
  const peak = Math.max(0, ...totals);

  if (!peak) {
    return <div style={{ color: C.faint, fontSize: 12.5, padding: '28px 0', textAlign: 'center' }}>No premium written in this window.</div>;
  }

  // Round the axis up to 1, 2 or 5 times a power of ten, so the gridlines
  // land on numbers a reader can hold in their head.
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const step = [1, 2, 5, 10].map(f => f * magnitude).find(v => v >= peak) || peak;
  const ticks = [step, step * 0.75, step * 0.5, step * 0.25, 0];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          height, fontSize: 10.5, color: C.faint, textAlign: 'right', flexShrink: 0,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {ticks.map(t => <span key={t}>{t === 0 ? '0' : moneyShort(t, '')}</span>)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            position: 'relative', height, display: 'flex', alignItems: 'flex-end',
            gap: 'clamp(6px, 2%, 18px)', borderBottom: `1px solid ${C.border}`,
          }}>
            {/* Gridlines behind the columns, not over them. */}
            {ticks.slice(0, -1).map((t, i) => (
              <span
                key={t}
                aria-hidden='true'
                style={{
                  position: 'absolute', left: 0, right: 0, top: `${(i / (ticks.length - 1)) * 100}%`,
                  borderTop: `1px dashed ${C.hair}`,
                }}
              />
            ))}
            {months.map((m) => {
              const total = keys.reduce((a, k) => a + (Number(m[k.key]) || 0), 0);
              return (
                <div
                  key={`${m.year}-${m.key || m.label}`}
                  title={`${m.label} — ${money0(total, currency)}`}
                  style={{
                    flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                    justifyContent: 'flex-end', height: '100%', position: 'relative',
                  }}
                >
                  {keys.map((k) => {
                    const v = Number(m[k.key]) || 0;
                    if (!v) return null;
                    return (
                      <span
                        key={k.key}
                        style={{ display: 'block', height: `${(v / step) * 100}%`, background: k.colour }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 'clamp(6px, 2%, 18px)', marginTop: 7 }}>
            {months.map(m => (
              <span key={`${m.year}-${m.key || m.label}-lbl`} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 11, color: C.muted }}>
                {m.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        {keys.map(k => (
          <span key={k.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: C.body }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: k.colour }} />
            {k.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Horizontal bar list ──────────────────────────────────────
// Geographic exposure. Bars are scaled against the LARGEST row rather than
// against 100%, so the shape of the distribution is visible even when the
// book is spread thinly; the percentage beside each row carries the absolute
// reading.
export function BarList({ segments = [], max, colour = '#8a1538' }) {
  const rows = segments.filter(s => Number(s.amount) > 0);
  if (!rows.length) {
    return <div style={{ color: C.faint, fontSize: 12.5, padding: '20px 0', textAlign: 'center' }}>Nothing to show.</div>;
  }
  const peak = max || Math.max(...rows.map(s => Number(s.amount)));
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 11 }}>
      {rows.map((s, i) => (
        <li key={s.label} style={{ display: 'grid', gridTemplateColumns: 'minmax(70px, 110px) 1fr 44px', alignItems: 'center', gap: 11 }}>
          <span style={{ fontSize: 12.5, color: C.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.label}
          </span>
          <span style={{ display: 'block', height: 9, borderRadius: 3, background: C.barTrack, overflow: 'hidden' }}>
            <span style={{
              display: 'block',
              width: `${peak ? (Number(s.amount) / peak) * 100 : 0}%`,
              height: '100%',
              // Step the tint down the list so a long ranking does not read
              // as one solid block.
              background: i === 0 ? colour : seriesColour(i),
              borderRadius: 3,
            }} />
          </span>
          <span style={{ fontSize: 12, color: C.ink, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {s.percent === null || s.percent === undefined ? '—' : `${Math.round(s.percent)}%`}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── Insight card ─────────────────────────────────────────────
// One reading from the portfolio with the sentence that explains it. The
// figure is large because it is the point; the sentence is what makes it
// actionable, so it is not optional.
export function InsightCard({ icon, title, value, detail, tone = 'blue' }) {
  const tint = {
    blue:   { fg: C.blue,   bg: C.blueBg },
    amber:  { fg: C.amber,  bg: C.amberBg },
    red:    { fg: C.red,    bg: C.redBg },
    green:  { fg: C.green,  bg: C.greenBg },
  }[tone] || { fg: C.blue, bg: C.blueBg };

  return (
    <section style={{
      border: `1px solid ${C.border}`, borderRadius: 10, padding: '13px 14px',
      background: '#fff', display: 'grid', gridTemplateColumns: '32px 1fr', gap: 11,
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: 8, background: tint.bg, color: tint.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 700, color: C.ink }}>{title}</h3>
        <p style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: C.ink, lineHeight: 1.1 }}>
          {value === null || value === undefined ? <span style={{ color: C.faint, fontSize: 15 }}>Not available</span> : value}
        </p>
        {detail && <p style={{ margin: 0, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{detail}</p>}
      </div>
    </section>
  );
}

const charts = { Ring, ScoreBar, Donut, StackedBars, BarList, InsightCard, SERIES };
export default charts;
